import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Clock, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/posts`)
      .then((res) => setPosts(res.data))
      .catch(() => setError("We couldn't load the articles right now. Please check back soon."));
  }, []);

  return (
    <div data-testid="blog-page" className="pt-16">
      <SEO path="/tax-tips-blog"
        title="Tax Tips Blog | Marizema Tax Service — Tampa, FL"
        description="Practical tax tips and financial insights from Marizema Tax Service LLC — deductions, deadlines and smarter money habits for Tampa, FL." />
      <section className="py-16 sm:py-24 bg-[#0B2545]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-widest font-semibold text-[#D4AF37] mb-3">Tax Tips Blog</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-bold tracking-tight text-white max-w-3xl">
            Tax tips & financial insights
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
            Practical guidance from our Tampa team — deductions, deadlines, and smarter money habits.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {error && <p data-testid="blog-error" className="text-sm text-red-600">{error}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, i) => (
              <motion.article key={p.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.45 }}>
                <Link to={`/tax-tips-blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                  className="group block bg-white border border-slate-200 hover:border-[#C9A227]/60 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-300/60 h-full">
                  <div className="h-44 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-[11px] uppercase tracking-wide">
                      <span className="text-[#86680A] font-semibold">{p.category}</span>
                      <span className="text-slate-500">{p.date}</span>
                    </div>
                    <h2 className="mt-2.5 font-serif-display text-lg font-semibold text-[#0B2545] group-hover:text-[#86680A] transition-colors leading-snug">{p.title}</h2>
                    <p className="mt-2 text-sm text-[#1F2933]/75 leading-relaxed">{p.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-xs text-slate-500"><Clock size={13} /> {p.read_time}</span>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#86680A]">Read <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" /></span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
